// LOGOMAN PRO — pattern shape builders for icon + container overlays.
// All shapes are emitted in the target's local coordinate space (vb).
// Caller is responsible for clipping (clipPath) to the icon/container outline.

import type { IconPattern } from "../state/logoStore";

interface VB { x: number; y: number; w: number; h: number; }

/** Tiny seeded PRNG so "random" patterns stay stable between renders. */
function rng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 0xffffffff;
  };
}

/**
 * Build pattern overlay shapes.
 * `density` multiplies the count of pattern elements (1.0 = default,
 * 0.5 = sparser, 2.0 = denser). Clamped to 0.3..3 internally.
 */
export function buildIconPatternShapes(
  pattern: IconPattern,
  vb: VB,
  color: string,
  density = 1
): string {
  const d = Math.max(0.3, Math.min(3, density));
  const round = (n: number) => Math.max(2, Math.round(n));
  switch (pattern) {
    case "stripes-h":      return stripes(vb, color, round(8 * d),  0.5,  "h");
    case "stripes-v":      return stripes(vb, color, round(8 * d),  0.5,  "v");
    case "stripes-thin":   return stripes(vb, color, round(24 * d), 0.25, "h");
    case "stripes-thick":  return stripes(vb, color, round(4 * d),  0.55, "h");
    case "stripes-double": return doubleStripes(vb, color, round(5 * d));
    case "stripes-broken": return brokenStripes(vb, color, round(8 * d));
    case "stripes-diag":   return diagStripes(vb, color, round(12 * d));
    case "stripes-wavy":   return wavyStripes(vb, color, round(6 * d));
    case "dots":           return dots(vb, color, round(10 * d), 0.32);
    case "dots-tiny":      return dots(vb, color, round(18 * d), 0.20);
    case "dots-large":     return dots(vb, color, round(6 * d),  0.45);
    case "dots-random":    return randomDots(vb, color, round(60 * d));
    case "bubble-dots":    return bubbleDots(vb, color, round(6 * d));
    case "halftone-fade":  return halftoneFade(vb, color, round(10 * d));
    case "grid":           return gridLines(vb, color, round(8 * d),  0.08);
    case "grid-micro":     return gridLines(vb, color, round(20 * d), 0.04);
    case "grid-blueprint": return blueprintGrid(vb, color, d);
    case "checkerboard":   return checkerboard(vb, color, round(8 * d));
    case "checkerboard-tiny": return checkerboard(vb, color, round(16 * d));
    case "honeycomb":      return honeycomb(vb, color, round(7 * d));
    case "crosshatch":     return crosshatch(vb, color, round(18 * d));
    case "waves":          return waves(vb, color, round(5 * d));
    case "sound-waves":    return soundWaves(vb, color, round(18 * d));
    case "heartbeat":      return heartbeat(vb, color);
    case "topographic":    return topographic(vb, color);
    case "circuit":        return circuit(vb, color, round(14 * d));
    case "starburst":      return starburst(vb, color, round(24 * d));
    case "concentric-rings": return concentricRings(vb, color, round(8 * d));
    case "carbon-fiber":   return carbonFiber(vb, color, round(12 * d));
    case "bokeh":          return bokeh(vb, color, round(18 * d));
    case "confetti":       return confetti(vb, color, round(40 * d));
    case "speed-lines":    return speedLines(vb, color, round(12 * d));
    default:               return "";
  }
}

// ============================================================
// Stripes family
// ============================================================

function stripes(vb: VB, color: string, count: number, duty: number, dir: "h" | "v"): string {
  const out: string[] = [];
  if (dir === "h") {
    const gap = vb.h / count;
    const sw = gap * duty;
    for (let i = 0; i < count; i++) {
      const yy = vb.y + i * gap + (gap - sw) / 2;
      out.push(`<rect x="${vb.x}" y="${yy}" width="${vb.w}" height="${sw}" fill="${color}"/>`);
    }
  } else {
    const gap = vb.w / count;
    const sw = gap * duty;
    for (let i = 0; i < count; i++) {
      const xx = vb.x + i * gap + (gap - sw) / 2;
      out.push(`<rect x="${xx}" y="${vb.y}" width="${sw}" height="${vb.h}" fill="${color}"/>`);
    }
  }
  return out.join("");
}

function diagStripes(vb: VB, color: string, stripes = 12): string {
  const ext = Math.max(vb.w, vb.h) * 1.6;
  const gap = ext / stripes;
  const sw = gap * 0.5;
  const cx = vb.x + vb.w / 2, cy = vb.y + vb.h / 2;
  let out = `<g transform="rotate(45 ${cx} ${cy})">`;
  for (let i = -1; i <= stripes; i++) {
    const yy = cy - ext / 2 + i * gap;
    out += `<rect x="${cx - ext / 2}" y="${yy}" width="${ext}" height="${sw}" fill="${color}"/>`;
  }
  return out + `</g>`;
}

function doubleStripes(vb: VB, color: string, bands = 5): string {
  const out: string[] = [];
  const bandH = vb.h / bands;
  const lineH = bandH * 0.10;
  const sep = bandH * 0.20;
  for (let i = 0; i < bands; i++) {
    const cy = vb.y + (i + 0.5) * bandH;
    out.push(`<rect x="${vb.x}" y="${cy - sep / 2 - lineH}" width="${vb.w}" height="${lineH}" fill="${color}"/>`);
    out.push(`<rect x="${vb.x}" y="${cy + sep / 2}" width="${vb.w}" height="${lineH}" fill="${color}"/>`);
  }
  return out.join("");
}

function brokenStripes(vb: VB, color: string, rows = 8): string {
  const out: string[] = [];
  const gap = vb.h / rows;
  const sh = gap * 0.4;
  const dashLen = vb.w / 8;
  const dashGap = dashLen * 0.5;
  for (let i = 0; i < rows; i++) {
    const yy = vb.y + i * gap + (gap - sh) / 2;
    let xx = vb.x + (i % 2 === 0 ? 0 : dashLen / 2);
    while (xx < vb.x + vb.w) {
      out.push(`<rect x="${xx}" y="${yy}" width="${dashLen}" height="${sh}" fill="${color}"/>`);
      xx += dashLen + dashGap;
    }
  }
  return out.join("");
}

function wavyStripes(vb: VB, color: string, rows = 6): string {
  const out: string[] = [];
  const stepW = vb.w / 24;
  const amp = vb.h * 0.018;
  const sw = vb.h / rows * 0.25;
  for (let i = 0; i < rows; i++) {
    const baseY = vb.y + (i + 0.5) * (vb.h / rows);
    let d = `M ${vb.x} ${baseY}`;
    for (let x = 0; x <= 24; x++) {
      const xx = vb.x + x * stepW;
      const yy = baseY + Math.sin(x * 0.9 + i) * amp;
      d += ` L ${xx} ${yy}`;
    }
    out.push(`<path d="${d}" stroke="${color}" stroke-width="${sw}" fill="none" stroke-linecap="round"/>`);
  }
  return out.join("");
}

// ============================================================
// Dots family
// ============================================================

function dots(vb: VB, color: string, gridSize: number, dotR: number): string {
  const cellW = vb.w / gridSize, cellH = vb.h / gridSize;
  const r = cellW * dotR;
  const out: string[] = [];
  for (let row = 0; row < gridSize; row++) {
    for (let col = 0; col < gridSize; col++) {
      const cx = vb.x + (col + 0.5) * cellW;
      const cy = vb.y + (row + 0.5) * cellH;
      out.push(`<circle cx="${cx}" cy="${cy}" r="${r}" fill="${color}"/>`);
    }
  }
  return out.join("");
}

function randomDots(vb: VB, color: string, count = 60): string {
  const r = rng(8675309);
  const baseR = Math.min(vb.w, vb.h) * 0.04;
  const out: string[] = [];
  for (let i = 0; i < count; i++) {
    const cx = vb.x + r() * vb.w;
    const cy = vb.y + r() * vb.h;
    const radius = baseR * (0.6 + r() * 0.8);
    out.push(`<circle cx="${cx}" cy="${cy}" r="${radius}" fill="${color}"/>`);
  }
  return out.join("");
}

function bubbleDots(vb: VB, color: string, cells = 6): string {
  const r = rng(424242);
  const cols = cells, rows = cells;
  const cellW = vb.w / cols, cellH = vb.h / rows;
  const out: string[] = [];
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const cx = vb.x + (col + 0.5) * cellW;
      const cy = vb.y + (row + 0.5) * cellH;
      const radius = cellW * (0.18 + r() * 0.28);
      out.push(`<circle cx="${cx}" cy="${cy}" r="${radius}" fill="${color}"/>`);
    }
  }
  return out.join("");
}

function halftoneFade(vb: VB, color: string, cells = 10): string {
  const cols = cells, rows = cells;
  const cellW = vb.w / cols, cellH = vb.h / rows;
  const out: string[] = [];
  for (let row = 0; row < rows; row++) {
    const t = row / (rows - 1);
    const dotR = cellW * (0.10 + 0.30 * t);
    for (let col = 0; col < cols; col++) {
      const cx = vb.x + (col + 0.5) * cellW;
      const cy = vb.y + (row + 0.5) * cellH;
      out.push(`<circle cx="${cx}" cy="${cy}" r="${dotR}" fill="${color}"/>`);
    }
  }
  return out.join("");
}

// ============================================================
// Grid family
// ============================================================

function gridLines(vb: VB, color: string, cells: number, lineRatio: number): string {
  const lineW = (vb.w / cells) * lineRatio;
  const out: string[] = [];
  for (let i = 1; i < cells; i++) {
    const xx = vb.x + i * (vb.w / cells);
    const yy = vb.y + i * (vb.h / cells);
    out.push(`<rect x="${xx - lineW / 2}" y="${vb.y}" width="${lineW}" height="${vb.h}" fill="${color}"/>`);
    out.push(`<rect x="${vb.x}" y="${yy - lineW / 2}" width="${vb.w}" height="${lineW}" fill="${color}"/>`);
  }
  return out.join("");
}

function blueprintGrid(vb: VB, color: string, density = 1): string {
  // Major grid every 1/4, minor crosses every 1/16 with tiny tick marks
  const out: string[] = [];
  const minor = Math.max(8, Math.round(16 * density));
  const major = Math.max(2, Math.round(4 * density));
  const minorStep = vb.w / minor, majorStep = vb.w / major;
  const minorW = vb.w * 0.004;
  const majorW = vb.w * 0.012;
  for (let i = 1; i < minor; i++) {
    if (i % (minor / major) === 0) continue;
    const xx = vb.x + i * minorStep;
    out.push(`<rect x="${xx - minorW / 2}" y="${vb.y}" width="${minorW}" height="${vb.h}" fill="${color}" opacity="0.45"/>`);
    const yy = vb.y + i * (vb.h / minor);
    out.push(`<rect x="${vb.x}" y="${yy - minorW / 2}" width="${vb.w}" height="${minorW}" fill="${color}" opacity="0.45"/>`);
  }
  for (let i = 1; i < major; i++) {
    const xx = vb.x + i * majorStep;
    out.push(`<rect x="${xx - majorW / 2}" y="${vb.y}" width="${majorW}" height="${vb.h}" fill="${color}"/>`);
    const yy = vb.y + i * (vb.h / major);
    out.push(`<rect x="${vb.x}" y="${yy - majorW / 2}" width="${vb.w}" height="${majorW}" fill="${color}"/>`);
  }
  return out.join("");
}

// ============================================================
// Geometric family (checker, honeycomb, crosshatch)
// ============================================================

function checkerboard(vb: VB, color: string, cells: number): string {
  const sw = vb.w / cells, sh = vb.h / cells;
  const out: string[] = [];
  for (let row = 0; row < cells; row++) {
    for (let col = 0; col < cells; col++) {
      if ((row + col) % 2 === 0) continue;
      const xx = vb.x + col * sw;
      const yy = vb.y + row * sh;
      out.push(`<rect x="${xx}" y="${yy}" width="${sw}" height="${sh}" fill="${color}"/>`);
    }
  }
  return out.join("");
}

function honeycomb(vb: VB, color: string, cols = 7): string {
  // Hex grid using flat-top hexagons
  const hexW = vb.w / cols;
  const hexH = hexW * 1.1547;        // height of a hexagon with width hexW
  const rowGap = hexH * 0.75;
  const lineW = hexW * 0.06;
  const out: string[] = [];
  for (let row = -1; row * rowGap < vb.h + hexH; row++) {
    for (let col = -1; col < cols + 1; col++) {
      const cx = vb.x + col * hexW + (row % 2 === 0 ? 0 : hexW / 2);
      const cy = vb.y + row * rowGap;
      const points: string[] = [];
      for (let k = 0; k < 6; k++) {
        const ang = (Math.PI / 3) * k + Math.PI / 6;
        const px = cx + (hexW / 2) * Math.cos(ang);
        const py = cy + (hexW / 2) * Math.sin(ang);
        points.push(`${px.toFixed(2)},${py.toFixed(2)}`);
      }
      out.push(`<polygon points="${points.join(" ")}" fill="none" stroke="${color}" stroke-width="${lineW}"/>`);
    }
  }
  return out.join("");
}

function crosshatch(vb: VB, color: string, lines = 18): string {
  // 45° + 135° thin lines
  const ext = Math.max(vb.w, vb.h) * 1.6;
  const gap = ext / lines;
  const sw = gap * 0.10;
  const cx = vb.x + vb.w / 2, cy = vb.y + vb.h / 2;
  let out = "";
  for (const angle of [45, -45]) {
    out += `<g transform="rotate(${angle} ${cx} ${cy})">`;
    for (let i = -1; i <= lines; i++) {
      const yy = cy - ext / 2 + i * gap;
      out += `<rect x="${cx - ext / 2}" y="${yy}" width="${ext}" height="${sw}" fill="${color}"/>`;
    }
    out += `</g>`;
  }
  return out;
}

// ============================================================
// Wave family
// ============================================================

function waves(vb: VB, color: string, count: number): string {
  const out: string[] = [];
  const stepW = vb.w / 32;
  const amp = vb.h * 0.05;
  const sw = vb.h * 0.012;
  for (let i = 0; i < count; i++) {
    const baseY = vb.y + (i + 0.5) * (vb.h / count);
    let d = `M ${vb.x} ${baseY}`;
    for (let x = 0; x <= 32; x++) {
      const xx = vb.x + x * stepW;
      const yy = baseY + Math.sin(x * 0.5 + i * 0.7) * amp;
      d += ` L ${xx.toFixed(2)} ${yy.toFixed(2)}`;
    }
    out.push(`<path d="${d}" stroke="${color}" stroke-width="${sw}" fill="none" stroke-linecap="round"/>`);
  }
  return out.join("");
}

function soundWaves(vb: VB, color: string, bars = 18): string {
  // Vertical bars centered, varying heights — equalizer style
  const gap = vb.w / bars;
  const barW = gap * 0.55;
  const cy = vb.y + vb.h / 2;
  const out: string[] = [];
  for (let i = 0; i < bars; i++) {
    const t = i / (bars - 1);
    const h = vb.h * (0.10 + 0.85 * Math.abs(Math.sin(t * Math.PI * 2.4 + 0.3)));
    const xx = vb.x + i * gap + (gap - barW) / 2;
    out.push(`<rect x="${xx}" y="${cy - h / 2}" width="${barW}" height="${h}" rx="${barW / 3}" fill="${color}"/>`);
  }
  return out.join("");
}

function heartbeat(vb: VB, color: string): string {
  const cy = vb.y + vb.h / 2;
  const sw = vb.h * 0.025;
  // Two ECG cycles across the width
  const cycles = 2;
  let d = `M ${vb.x} ${cy}`;
  for (let c = 0; c < cycles; c++) {
    const baseX = vb.x + c * (vb.w / cycles);
    const stepX = (vb.w / cycles) / 12;
    d += ` L ${baseX + 4 * stepX} ${cy}`;
    d += ` L ${baseX + 5 * stepX} ${cy - vb.h * 0.10}`;
    d += ` L ${baseX + 6 * stepX} ${cy + vb.h * 0.30}`;
    d += ` L ${baseX + 7 * stepX} ${cy - vb.h * 0.40}`;
    d += ` L ${baseX + 8 * stepX} ${cy + vb.h * 0.05}`;
    d += ` L ${baseX + (vb.w / cycles)} ${cy}`;
  }
  return `<path d="${d}" stroke="${color}" stroke-width="${sw}" fill="none" stroke-linecap="round" stroke-linejoin="round"/>`;
}

function topographic(vb: VB, color: string): string {
  // Concentric rounded blobs offset to feel like terrain contours
  const out: string[] = [];
  const sw = vb.w * 0.006;
  const cx = vb.x + vb.w * 0.55, cy = vb.y + vb.h * 0.5;
  for (let i = 1; i <= 8; i++) {
    const rx = vb.w * (0.05 + i * 0.07);
    const ry = vb.h * (0.04 + i * 0.06);
    out.push(`<ellipse cx="${cx}" cy="${cy}" rx="${rx}" ry="${ry}" fill="none" stroke="${color}" stroke-width="${sw}"/>`);
  }
  // Second smaller cluster
  const cx2 = vb.x + vb.w * 0.18, cy2 = vb.y + vb.h * 0.25;
  for (let i = 1; i <= 4; i++) {
    const rx = vb.w * (0.03 + i * 0.04);
    const ry = vb.h * (0.025 + i * 0.035);
    out.push(`<ellipse cx="${cx2}" cy="${cy2}" rx="${rx}" ry="${ry}" fill="none" stroke="${color}" stroke-width="${sw}"/>`);
  }
  return out.join("");
}

// ============================================================
// Specialty (circuit, starburst, rings, carbon, bokeh, confetti)
// ============================================================

function circuit(vb: VB, color: string, traces = 14): string {
  // Right-angle traces with terminal pads — abstract microchip vibe
  const out: string[] = [];
  const sw = vb.w * 0.012;
  const padR = vb.w * 0.02;
  const r = rng(13579);
  const grid = 8;
  const cellW = vb.w / grid, cellH = vb.h / grid;
  for (let i = 0; i < traces; i++) {
    const x0 = vb.x + Math.floor(r() * grid) * cellW;
    const y0 = vb.y + Math.floor(r() * grid) * cellH;
    const len1 = (1 + Math.floor(r() * 3)) * cellW;
    const len2 = (1 + Math.floor(r() * 3)) * cellH;
    const x1 = x0 + (r() < 0.5 ? len1 : -len1);
    const y1 = y0;
    const x2 = x1, y2 = y0 + (r() < 0.5 ? len2 : -len2);
    out.push(`<polyline points="${x0},${y0} ${x1},${y1} ${x2},${y2}" fill="none" stroke="${color}" stroke-width="${sw}" stroke-linecap="round" stroke-linejoin="round"/>`);
    out.push(`<circle cx="${x0}" cy="${y0}" r="${padR}" fill="${color}"/>`);
    out.push(`<circle cx="${x2}" cy="${y2}" r="${padR}" fill="${color}"/>`);
  }
  return out.join("");
}

function starburst(vb: VB, color: string, rays = 24): string {
  const cx = vb.x + vb.w / 2, cy = vb.y + vb.h / 2;
  const inner = vb.w * 0.05;
  const outer = Math.max(vb.w, vb.h);
  const out: string[] = [];
  for (let i = 0; i < rays; i++) {
    const a = (Math.PI * 2 * i) / rays;
    const half = (Math.PI / rays) * 0.45;
    const x1 = cx + Math.cos(a - half) * inner;
    const y1 = cy + Math.sin(a - half) * inner;
    const x2 = cx + Math.cos(a + half) * inner;
    const y2 = cy + Math.sin(a + half) * inner;
    const x3 = cx + Math.cos(a) * outer;
    const y3 = cy + Math.sin(a) * outer;
    out.push(`<polygon points="${x1},${y1} ${x2},${y2} ${x3},${y3}" fill="${color}"/>`);
  }
  return out.join("");
}

function concentricRings(vb: VB, color: string, rings = 8): string {
  const cx = vb.x + vb.w / 2, cy = vb.y + vb.h / 2;
  const sw = vb.w * 0.012;
  const maxR = Math.min(vb.w, vb.h) * 0.5;
  const out: string[] = [];
  for (let i = 1; i <= rings; i++) {
    out.push(`<circle cx="${cx}" cy="${cy}" r="${(i / rings) * maxR}" fill="none" stroke="${color}" stroke-width="${sw}"/>`);
  }
  return out.join("");
}

function carbonFiber(vb: VB, color: string, cells = 12): string {
  // Interleaved squares — woven carbon look
  const sw = vb.w / cells, sh = vb.h / cells;
  const out: string[] = [];
  for (let row = 0; row < cells; row++) {
    for (let col = 0; col < cells; col++) {
      if ((row + col) % 2 !== 0) continue;
      const xx = vb.x + col * sw + sw * 0.08;
      const yy = vb.y + row * sh + sh * 0.08;
      out.push(`<rect x="${xx}" y="${yy}" width="${sw * 0.84}" height="${sh * 0.84}" rx="${sw * 0.18}" fill="${color}"/>`);
    }
  }
  return out.join("");
}

function bokeh(vb: VB, color: string, count = 18): string {
  const r = rng(91827);
  const out: string[] = [];
  for (let i = 0; i < count; i++) {
    const cx = vb.x + r() * vb.w;
    const cy = vb.y + r() * vb.h;
    const radius = vb.w * (0.06 + r() * 0.10);
    const opacity = (0.25 + r() * 0.45).toFixed(2);
    out.push(`<circle cx="${cx}" cy="${cy}" r="${radius}" fill="${color}" opacity="${opacity}"/>`);
  }
  return out.join("");
}

function speedLines(vb: VB, color: string, count = 12): string {
  const out: string[] = [];
  const angle = -18;
  const lineH = vb.h * 0.018;
  const len = vb.w * 0.55;
  for (let i = 0; i < count; i++) {
    const t = i / Math.max(1, count - 1);
    const x = vb.x - vb.w * 0.1 + t * vb.w * 0.7;
    const y = vb.y + (0.05 + t * 0.85) * vb.h;
    const cx = x + len / 2;
    const cy = y + lineH / 2;
    out.push(
      `<rect x="${x}" y="${y}" width="${len}" height="${lineH}" rx="${lineH / 2}" fill="${color}" opacity="0.55" transform="rotate(${angle} ${cx} ${cy})"/>`
    );
  }
  return out.join("");
}

function confetti(vb: VB, color: string, count = 40): string {
  const r = rng(20260427);
  const out: string[] = [];
  const baseW = vb.w * 0.025, baseH = vb.h * 0.012;
  for (let i = 0; i < count; i++) {
    const cx = vb.x + r() * vb.w;
    const cy = vb.y + r() * vb.h;
    const angle = r() * 360;
    const w = baseW * (0.7 + r() * 0.8);
    const h = baseH * (0.7 + r() * 0.8);
    out.push(`<rect x="${cx - w / 2}" y="${cy - h / 2}" width="${w}" height="${h}" fill="${color}" transform="rotate(${angle.toFixed(1)} ${cx} ${cy})"/>`);
  }
  return out.join("");
}
